import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  public render() {
    if (this.state.hasError) {
      let errorMessage = this.state.error?.message || 'An unexpected error occurred.';
      let isFirestoreError = false;
      let firestoreErrorInfo = null;

      try {
        const parsedError = JSON.parse(errorMessage);
        if (parsedError && parsedError.operationType) {
          isFirestoreError = true;
          firestoreErrorInfo = parsedError;
          errorMessage = parsedError.error || errorMessage;
        }
      } catch (e) {
        // Not a JSON string, ignore
      }

      return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-stone-100">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6 mx-auto">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-black text-center text-bark mb-4">Something went wrong</h2>
            
            <div className="bg-stone-50 p-4 rounded-xl mb-6 overflow-auto max-h-48 text-sm text-stone-600 font-mono">
              {isFirestoreError ? (
                <div>
                  <p className="font-bold text-red-600 mb-2">Database Permission Error</p>
                  <p><strong>Operation:</strong> {firestoreErrorInfo.operationType}</p>
                  <p><strong>Path:</strong> {firestoreErrorInfo.path}</p>
                  <p><strong>Details:</strong> {errorMessage}</p>
                </div>
              ) : (
                <p>{errorMessage}</p>
              )}
            </div>

            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-palm text-white rounded-xl font-bold hover:bg-bark transition"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
